import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Box, Tabs, Tab, Typography, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Chip
} from '@mui/material';

const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID');
  }
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'dosen_approved':
      return 'success';
    case 'dosen_rejected':
      return 'error';
    case 'admin_approved':
      return 'success';
    case 'admin_rejected':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'dosen_approved':
      return 'Disetujui Dosen';
    case 'dosen_rejected':
      return 'Ditolak Dosen';
    case 'admin_approved':
      return 'Disetujui Admin';
    case 'admin_rejected':
      return 'Ditolak Admin';
    case 'pending':
      return 'Menunggu Persetujuan';
    default:
      return status;
  }
};

const getApproverText = (approver) => {
  if (!approver) return '';
  switch (approver.role) {
    case 'dosen':
      return 'Disetujui oleh Dosen';
    case 'admin':
      return 'Disetujui oleh Admin';
    case 'laboran':
      return 'Disetujui oleh Laboran';
    default:
      return `Disetujui oleh ${approver.role}`;
  }
};

const RiwayatPengajuanDosen = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // State for each type of request
  const [bahanRequests, setBahanRequests] = useState([]);
  const [alatRequests, setAlatRequests] = useState([]);
  const [instrumenRequests, setInstrumenRequests] = useState([]);

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setError('Silakan login.');
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch requests
  const fetchRequests = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError('');
    try {
      // Fetch bahan requests
      const bahanRef = collection(db, 'permintaan_bahan');
      
      // Query for requests where dosenPengampuId matches
      const bahanQuery1 = query(
        bahanRef,
        where('dosenPengampuId', '==', currentUser.uid)
      );
      
      // Query for requests where dosenPembimbingId matches
      const bahanQuery2 = query(
        bahanRef,
        where('dosenPembimbingId', '==', currentUser.uid)
      );

      // Execute both queries
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(bahanQuery1),
        getDocs(bahanQuery2)
      ]);

      // Combine results using Map to avoid duplicates
      const bahanMap = new Map();
      
      snapshot1.docs.forEach(doc => {
        if (!bahanMap.has(doc.id)) {
          bahanMap.set(doc.id, { id: doc.id, ...doc.data() });
        }
      });
      
      snapshot2.docs.forEach(doc => {
        if (!bahanMap.has(doc.id)) {
          bahanMap.set(doc.id, { id: doc.id, ...doc.data() });
        }
      });

      // Convert Map to array and sort
      const bahanData = Array.from(bahanMap.values())
        .sort((a, b) => b.tanggalPermintaan?.seconds - a.tanggalPermintaan?.seconds);
      
      setBahanRequests(bahanData);

      // Fetch alat requests
      const alatRef = collection(db, 'permintaan_alat');
      
      // Query for alat gelas requests
      const alatQuery1 = query(
        alatRef,
        where('dosenTerkaitId', '==', currentUser.uid),
        where('jenisAlat', '==', 'alat_gelas')
      );
      
      const alatSnapshot = await getDocs(alatQuery1);
      const alatData = alatSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => b.tanggalPermintaan?.seconds - a.tanggalPermintaan?.seconds);
      setAlatRequests(alatData);

      // Query for instrumen requests
      const instrumenQuery1 = query(
        alatRef,
        where('dosenTerkaitId', '==', currentUser.uid),
        where('jenisAlat', '==', 'instrumen')
      );
      
      const instrumenSnapshot = await getDocs(instrumenQuery1);
      const instrumenData = instrumenSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => b.tanggalPermintaan?.seconds - a.tanggalPermintaan?.seconds);
      setInstrumenRequests(instrumenData);

    } catch (err) {
      console.error("Error fetching requests:", err);
      setError('Gagal memuat data riwayat.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchRequests();
    }
  }, [currentUser]);

  const renderTable = (requests) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nama Mahasiswa</TableCell>
            <TableCell>NIM</TableCell>
            <TableCell>Nama Item</TableCell>
            <TableCell>Jumlah</TableCell>
            <TableCell>Tujuan</TableCell>
            <TableCell>Tanggal Pengajuan</TableCell>
            <TableCell>Status</TableCell>
                <TableCell>Riwayat Persetujuan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell>{req.namaUser}</TableCell>
                  <TableCell>{req.userNim}</TableCell>
                  <TableCell>{req.namaBahan || req.namaAlat}</TableCell>
                  <TableCell>{req.jumlahDiminta || req.jumlah} {req.satuan}</TableCell>
                  <TableCell>{req.tujuanPenggunaan}</TableCell>
                  <TableCell>{formatDate(req.tanggalPermintaan)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(req.status)}
                      color={getStatusColor(req.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {req.approvalHistory ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {req.approvalHistory.map((history, index) => (
                          <Box key={index}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {history.approver.name} ({history.approver.role})
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              {history.action === 'approved' ? 'Menyetujui' : 'Menolak'} pada {formatDate(history.timestamp)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      'Tidak ada riwayat'
                    )}
                  </TableCell>
            </TableRow>
          ))}
          {requests.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                Tidak ada riwayat pengajuan
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading && !bahanRequests.length && !alatRequests.length && !instrumenRequests.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Memuat data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Riwayat Pengajuan Mahasiswa
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Bahan" />
          <Tab label="Alat Gelas" />
          <Tab label="Instrumen" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Typography variant="h6" gutterBottom>Riwayat Permintaan Bahan</Typography>
        {renderTable(bahanRequests)}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>Riwayat Permintaan Alat Gelas</Typography>
        {renderTable(alatRequests)}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>Riwayat Permintaan Instrumen</Typography>
        {renderTable(instrumenRequests)}
      </TabPanel>
    </Box>
  );
};

export default RiwayatPengajuanDosen;
